import {
  AssetAssignment,
  MessagingProtocol,
  BrokerConfigDto,
  MockService,
  MockServiceMar,
  TRANSACTIONAL,
  RunnerType,
} from 'types/rest/mockServiceTypes';
import _ from 'lodash';
import { MarFile } from 'store/marFiles/marFilesTypes';
import store from 'store/configureStore';
import { ApplicationState } from 'store/rootReducer';
import { CraneLocation } from 'types/rest/locationTypes';
import i18n from 'i18n';
import { Service } from 'types/rest/assetCatalogTypes';
import { getMockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplatesActions';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import { getErrorMessage, isTemplate } from './miscUtil';
import { processThinkTime } from './commonUtil';
import { BlazeAnalyticsRequest, Metrics } from 'types/rest/analyticsTypes';
import { getActiveWorkspaceId } from './bzmUserConfigUtil';
import { getHitAndMissesThunk } from 'store/analytics/data/analyticsDataActions';
import { pollTrackingService } from 'axios/axios-polling';
import * as actionTypes from 'constants/action-types';
import {
  dispatchDeploymentErrorMessage,
  dispatchDeploymentSuccesMessage,
  updateServiceMockThunk,
} from 'store/mockServices/mockServiceList/mockServiceListActions';
import { PROVIDER_CLOUD, PROVIDER_OPL, TRANSACTION_THRESHOLD_LIMIT } from 'constants/bzm-constants';
import { MockServiceBulkAction } from '../views/MockServices/Tabs/MockServices/MockServices';
import { Configuration, ConfigurationMap } from '../types/rest/configurationsType';
import { XmlNamespaceItem } from '../components/transactionEditor/BodyMatchersList';
import { Matcher } from '../types/rest/dslTypes';

const isFieldDirty = (field: any, originalField: any): boolean => {
  return (field && !originalField) || (!field && originalField) || (field && originalField && field !== originalField);
};

/**
 * Checks whether virtual service is BlazeVSE. It is SV type and does not have vse, registry and bridge assigned.
 */
export const isBlazeVseMockService = (mockService: MockServiceMar): boolean => {
  return (
    mockService.type === 'SV' &&
    _.isNil(mockService.vseId) &&
    _.isNil(mockService.registryId) &&
    _.isNil(mockService.bridgeId)
  );
};

/**
 * Returns true if virtual service was based on a template.
 * @param mockService
 */
export const hasAppliedTemplate = (mockService: MockService) => {
  return !_.isNil(mockService.appliedTemplateId);
};

/**
 * Compares the virtual service parent and applied template object if there is any applied template assigned to virtual service.
 * Returns true if there are differences.
 * @param mockService
 */
export const isAppliedTemplateDifferent = async (mockService: MockService) => {
  if (!hasAppliedTemplate(mockService)) {
    return false;
  }

  try {
    const template = (await getMockServiceTemplate(mockService.appliedTemplateId as number)).data.result;
    return !mockServiceEqualsTemplate(mockService, template);
  } catch (error) {
    const errorMsg = getErrorMessage(error);
    console.log(errorMsg);
  }

  return true;
};

const areTransactionIdsDirty = (mockService: MockService): boolean => {
  const mockServiceTxnIds = _.map(mockService.mockServiceTransactions, t => t.txnId);
  const originalMockServiceTxnIds = _.map(mockService.original.mockServiceTransactions, t => t.txnId);

  const areTxnIdsTheSame = _.isEqual(_.sortBy(mockServiceTxnIds), _.sortBy(originalMockServiceTxnIds));
  if (areTxnIdsTheSame) {
    let foundDifference = false;
    _.each(mockService.mockServiceTransactions, t => {
      const origT = _.find(mockService.original.mockServiceTransactions, origt => origt.txnId === t.txnId);
      if (origT) {
        if (origT.priority !== t.priority) {
          foundDifference = true;
          return false;
        }
      }
    });

    return foundDifference;
  } else {
    return true;
  }
};

export const isDirty = (mockService: MockService): boolean => {
  if (!mockService.original) {
    return false;
  }

  if (isFieldDirty(mockService.name, mockService.original.name)) return true;
  if (isFieldDirty(mockService.description, mockService.original.description)) return true;
  if (isFieldDirty(mockService.serviceId, mockService.original.serviceId)) return true;
  if (isFieldDirty(mockService.harborId, mockService.original.harborId)) return true;
  if (isFieldDirty(mockService.shipId, mockService.original.shipId)) return true;
  if (isFieldDirty(mockService.endpointPreference, mockService.original.endpointPreference)) return true;
  if (areTransactionIdsDirty(mockService)) return true;
  if (isFieldDirty(mockService.thinkTime, mockService.original.thinkTime)) return true;
  if (isFieldDirty(mockService.liveSystemPort, mockService.original.liveSystemPort)) return true;
  if (isFieldDirty(mockService.liveSystemHost, mockService.original.liveSystemHost)) return true;
  if (isFieldDirty(mockService.replicas, mockService.original.replicas)) return true;
  if (isFieldDirty(mockService.noMatchingRequestPreference, mockService.original.noMatchingRequestPreference))
    return true;
  if (isFieldDirty(mockService.groupTag, mockService.original.groupTag)) return true;
  if (isFieldDirty(mockService.capacity, mockService.original.capacity)) return true;
  if (isFieldDirty(mockService.assetId, mockService.original.assetId)) return true;
  if (isFieldDirty(mockService.vseId, mockService.original.vseId)) return true;
  if (isFieldDirty(mockService.registryId, mockService.original.registryId)) return true;
  if (isFieldDirty(mockService.bridgeId, mockService.original.bridgeId)) return true;

  return false;
};

/**
 * Creates patch body for partial updates of the virtual service objects. Only fields which were
 * modified are going to be present in the patch body.
 *
 * @param mockService
 */
export const buildPatchBody = (mockService: MockService): any => {
  const patchBody: any = {};

  if (isFieldDirty(mockService.name, mockService.original.name)) patchBody['name'] = mockService.name;
  if (isFieldDirty(mockService.description, mockService.original.description))
    patchBody['description'] = mockService.description;
  if (isFieldDirty(mockService.serviceId, mockService.original.serviceId))
    patchBody['serviceId'] = mockService.serviceId;
  if (isFieldDirty(mockService.harborId, mockService.original.harborId)) patchBody['harborId'] = mockService.harborId;
  if (isFieldDirty(mockService.shipId, mockService.original.shipId)) patchBody['shipId'] = mockService.shipId;
  if (isFieldDirty(mockService.endpointPreference, mockService.original.endpointPreference))
    patchBody['endpointPreference'] = mockService.endpointPreference;
  if (areTransactionIdsDirty(mockService)) patchBody['mockServiceTransactions'] = mockService.mockServiceTransactions;
  if (isFieldDirty(mockService.thinkTime, mockService.original.thinkTime))
    patchBody['thinkTime'] = mockService.thinkTime;
  if (isFieldDirty(mockService.liveSystemPort, mockService.original.liveSystemPort))
    patchBody['liveSystemPort'] = mockService.liveSystemPort;
  if (isFieldDirty(mockService.liveSystemHost, mockService.original.liveSystemHost))
    patchBody['liveSystemHost'] = mockService.liveSystemHost;
  if (isFieldDirty(mockService.noMatchingRequestPreference, mockService.original.noMatchingRequestPreference))
    patchBody['noMatchingRequestPreference'] = mockService.noMatchingRequestPreference;
  if (isFieldDirty(mockService.groupTag, mockService.original.groupTag)) patchBody['groupTag'] = mockService.groupTag;
  if (isFieldDirty(mockService.capacity, mockService.original.capacity)) patchBody['capacity'] = mockService.capacity;
  if (isFieldDirty(mockService.assetId, mockService.original.assetId)) patchBody['assetId'] = mockService.assetId;
  if (isFieldDirty(mockService.vseId, mockService.original.vseId)) patchBody['vseId'] = mockService.vseId;
  if (isFieldDirty(mockService.registryId, mockService.original.registryId))
    patchBody['registryId'] = mockService.registryId;
  if (isFieldDirty(mockService.bridgeId, mockService.original.bridgeId)) patchBody['bridgeId'] = mockService.bridgeId;
  if (isFieldDirty(mockService.replicas, mockService.original.replicas)) patchBody['replicas'] = mockService.replicas;
  return patchBody;
};

export const groupTagRegexp = '^[a-zA-Z0-9.\\-_\\$]*$';

export const isMSValid = (mockService: MockService): boolean => {
  let isValid = mockService.name.trim().length > 0;
  if (mockService.noMatchingRequestPreference === 'bypasslive') {
    isValid = isValid && !!mockService.liveSystemHost.match('^(http|https)://.*$');
  }

  if (mockService.replicas) {
    isValid = isValid && !(mockService.replicas > 10);
  }

  if (mockService.type === TRANSACTIONAL) {
    return isValid;
  }
  isValid = isValid && mockService.capacity >= 1;
  if (mockService.groupTag && mockService.groupTag.length > 0) {
    isValid = isValid && !!mockService.groupTag.substr(0, 1).match('[a-zA-Z0-9]');
    isValid = isValid && !!mockService.groupTag.match(groupTagRegexp);
  }
  isValid =
    isValid &&
    !_.isEmpty(mockService.thinkTime.toString()) &&
    mockService.thinkTime >= 0 &&
    mockService.thinkTime <= 1000000;
  return isValid;
};

export const TransportProtocolComponentCell = (rowData: MarFile) => {
  let transportProtocol = '-';
  if (rowData.primaryMetadata && !_.isEmpty(rowData.primaryMetadata.transportProtocol)) {
    transportProtocol = rowData.primaryMetadata.transportProtocol;
  }
  return transportProtocol;
};

export const VirtualServiceComponent = (rowData: MarFile) => {
  let virtualServiceName = '-';
  if (rowData.primaryMetadata && !_.isEmpty(rowData.primaryMetadata.name)) {
    virtualServiceName = rowData.primaryMetadata.name;
  }
  return virtualServiceName;
};

export const TransactionCountComponent = (rowData: MarFile) => {
  let txnCount = 0;
  if (rowData.primaryMetadata) {
    txnCount = rowData.primaryMetadata.txnCount;
  }
  return txnCount;
};

/** Checks whether location has kubernetes flag on. */
export const isLocationScalable = (harborId: string | null | undefined, shipId: string | null | undefined) => {
  // When ship or harbor is not present, not possible to scale
  if (!shipId || !harborId || harborId === null) {
    return false;
  }
  const location: CraneLocation = getLocationByShipAndHarbour(harborId, shipId);
  return location && location.isKubernetes;
};

/** Checks whether used location is an OPL one or not.*/
export const isOplLocation = (harborId: string, shipId: string) => {
  if (_.isNil(harborId) || _.isNil(shipId)) {
    return false;
  }

  const location: CraneLocation = getLocationByShipAndHarbour(harborId, shipId);
  if (_.isNil(location)) {
    return false;
  }

  return location.provider === PROVIDER_OPL;
};

/**
 * Gets location by shipId and harborId
 * @param harborId
 * @param shipId
 */
const getLocationByShipAndHarbour = (harborId: string, shipId: string) => {
  const state: ApplicationState = store.getState() as ApplicationState;
  const locations: CraneLocation[] = state.mockservices.locData;
  const location: CraneLocation | undefined = _.find(
    locations,
    (location: CraneLocation) => location.harborId === harborId && location.shipId === shipId,
  );
  return location;
};

/**
 * Returns the harbor's own default JVM heap (Xmx/Xms), in MB, for a given OPL location — used
 * to pre-fill the per-VS memory override fields when a user enables the override with no
 * existing values. Returns nulls if the location isn't loaded yet or the harbor didn't report
 * parseable values.
 */
export const getHarborDefaultHeapMb = (
  harborId: string,
  shipId: string,
): { xmx: number | null; xms: number | null } => {
  const location = getLocationByShipAndHarbour(harborId, shipId);
  const containerResources = location?.metadata?.containerResources;
  const xmx = Number(containerResources?.javaMaxHeapMb);
  const xms = Number(containerResources?.javaMinHeapMb);
  return {
    xmx: Number.isFinite(xmx) && xmx > 0 ? xmx : null,
    xms: Number.isFinite(xms) && xms > 0 ? xms : null,
  };
};

/** Check port range is available or not to display port details in the UI. */
export const isPortDisplayable = (harborId: string | null | undefined, shipId: string | null | undefined) => {
  if (!shipId || !harborId) {
    return false;
  }
  const state: ApplicationState = store.getState() as ApplicationState;
  const locations: CraneLocation[] = state.mockservices.locData;
  const location: CraneLocation | undefined = _.find(
    locations,
    (location: CraneLocation) => location.harborId === harborId && location.shipId === shipId,
  );
  return !!(location && !location.isKubernetes && location.metadata && location.metadata.portRange);
};

/**
 * We allow port editing for stopped and failed virtual services (eg. not running)
 * @param mockService
 */

export const isCellEditable = (mockService: MockService) => {
  if (mockService.metaData.inProgress) {
    return false;
  }
  const isNotRunning = mockService.status !== 'RUNNING';
  return mockService.isDraft === true || isNotRunning;
};

/** get docker OPL port range. */
export const portRange = (harborId: string | null | undefined, shipId: string | null | undefined) => {
  if (!shipId || !harborId) {
    return '';
  }
  const state: ApplicationState = store.getState() as ApplicationState;
  const locations: CraneLocation[] = state.mockservices.locData;
  const location: CraneLocation | undefined = _.find(
    locations,
    (location: CraneLocation) => location.harborId === harborId && location.shipId === shipId,
  );
  if (location && !location.isKubernetes && location.metadata && location.metadata.portRange)
    return location.metadata.portRange;
  else return '';
};

/**
 * Patch DTO is created automatically based on the changes of the original virtual service object and changed one.
 * There might be some properties that we do not want to truncate by the diff.
 * @param patchDto
 * @param patchedMockService
 */
export const sanitizeMockPatchDto = (patchDto: any, patchedMockService: MockService | MockServiceTemplate) => {
  const propertiesToCopy = [
    'dataSettings',
    'proxy',
    'mockServiceTransactions',
    'brokerConfig',
    'recorderConfigDto',
    'httpRecorderConfigDto',
    'loggingConfig',
    'authSettings',
  ];
  _.forEach(propertiesToCopy, property => {
    if (patchDto.hasOwnProperty(property)) {
      patchDto[property] = _.cloneDeep(patchedMockService[property]);
    }
  });

  //If we are patching either assets or configKey assets, make sure we send both attributes in a single patch object
  if (patchDto.hasOwnProperty('assets') || patchDto.hasOwnProperty('configKeyAssets')) {
    patchDto.assets = _.cloneDeep(patchedMockService.assets);
    patchDto.configKeyAssets = _.cloneDeep(patchedMockService.configKeyAssets);
  }

  // If we are switching to return404, we need to remove any CLIENT certs in a patch object
  if (patchDto.hasOwnProperty('noMatchingRequestPreference') && patchDto.noMatchingRequestPreference === 'return404') {
    patchDto.assets = _.filter(
      patchedMockService.assets,
      (asset: AssetAssignment) => asset.assetUsageType && asset.assetUsageType.startsWith('SERVER_'),
    );
  }

  patchDto = processThinkTime(patchDto, patchedMockService);

  // Get rid of field not needed for a patch
  delete patchDto.isDraft;
  delete patchDto.id;

  return patchDto;
};

/**
 * Generates location value which is the combination of harbor name and ship name.
 * @param harborId
 * @param shipId
 * @param locations list of locations
 * @param isPrivateData flag indicating whether private data feature is on (cloud not available in this setting)
 */
export const getLocValue = (
  harborId: string | null,
  shipId: string | null,
  locations: CraneLocation[],
  isPrivateData?: boolean,
) => {
  // Default fallback value if harbor and ship are not available
  if (harborId === null && shipId === null) {
    if (isPrivateData === true) {
      return 'Select Location';
    }
    return 'US East(Virginia)';
  }

  const harborFilter: any = locations.filter((value: any) => value.harborId === harborId);
  const shipFilter: any = harborFilter.filter((value: any) => value.shipId === shipId);

  if (shipFilter.length >= 1 && shipFilter[0].provider === 'Private Locations') {
    return shipFilter[0].harborName + ' - ' + shipFilter[0].shipName;
  } else if (shipFilter.length >= 1 && shipFilter[0].provider !== 'Private Locations') {
    return shipFilter[0].shipName;
  }
  return '';
};
/* It only returns true if a virtual service has a STOPPED status and it has been deployed before (It should have deploymentDateTime value)*/
export const isMSStopped = (mockService: MockService): boolean => {
  let isMSStopped = !_.isEmpty(mockService.status) && mockService.status.toUpperCase() === 'STOPPED';
  isMSStopped = isMSStopped && mockService.isNew !== true;
  return isMSStopped;
};

/**
 * Looks in the current state for cloud locations and returns the first one. If no Cloud providers are available
 * return the first available location.
 */
export const getDefaultLocation = (locations: CraneLocation[] | null) => {
  if (locations) {
    const cloudProviderLocations = locations.filter((location: CraneLocation) => {
      return location.provider === PROVIDER_CLOUD;
    });

    if (cloudProviderLocations.length !== 0) {
      return cloudProviderLocations[0];
    } else if (locations.length !== 0) {
      return locations[0];
    }
  }
  return null;
};

/**
 * Gets location string from combination of the ship & harbor. It looks in the redux store
 * in list of existing locations and tries to find the combo returning `{harborName} - {shipName}` string
 * @param harborId
 * @param shipId
 */
export const getLocationName = (harborId: string | null, shipId: string | null) => {
  const res = '-';
  if (_.isNil(harborId) || _.isNil(shipId)) {
    return res;
  }

  const locations = (store.getState() as any).mockservices.locData;
  if (_.isNil(locations)) {
    return res;
  }

  const location: CraneLocation | undefined = _.find(locations, (item: CraneLocation) => {
    return item.shipId === shipId;
  });

  if (location) {
    return location.harborName + ' - ' + location.shipName;
  }

  return res;
};

/**
 * Gets location string from a combination of the ship & harbor. It looks in the redux store
 * in a list of existing locations and tries to find the combo returning `{locationId}` number
 * @param harborId
 * @param shipId
 */
export const getLocationId = (harborId: string | null, shipId: string | null) => {
  const res = null;
  if (_.isNil(harborId) || _.isNil(shipId)) {
    return res;
  }

  const locations = (store.getState() as any).mockservices.locData;
  if (_.isNil(locations)) {
    return res;
  }

  const location: CraneLocation | undefined = _.find(locations, (item: CraneLocation) => {
    return item.shipId === shipId;
  });

  if (location) {
    return location.locationId;
  }

  return res;
};

export const getBulkPartialFailureErrorMessage = (
  errors: string[],
  trackingDtos: { serviceMockId: number; serviceMockName: string; trackingDto: { errors: string[] } }[],
  action: MockServiceBulkAction,
): string => {
  let errorMessage = '';
  if (!_.isEmpty(errors)) {
    const errorTrackingDtos = trackingDtos.filter(
      (t: { trackingDto: { errors: string[] } }) => t.trackingDto.errors.length > 0,
    );
    errorMessage = `${i18n.t('mockServices:errorMsgs.failedMockServiceNames', {
      action: action === null ? '' : action.toString().toLowerCase(),
      names: errorTrackingDtos.map((e: { serviceMockName: string }) => e.serviceMockName).join(','),
    })}`;

    const uniqueErrors = [...new Set(errors)];
    if (uniqueErrors.length === 1) {
      //Append reason if they all failed for same reason.
      errorMessage = errorMessage.concat(
        `${i18n.t('mockServices:errorMsgs.failedMockServiceNamesReason', {
          reason: uniqueErrors[0],
        })}`,
      );
    }
  }
  return errorMessage;
};

/**
 * Compares editable fields in the virtual service / template objects and returns true, if they are same.
 * @param mockService
 * @param template
 */
export const mockServiceEqualsTemplate = (mockService: MockService, template: MockServiceTemplate): boolean => {
  if (mockService.description !== template.description) return false;
  if (mockService.serviceId !== template.serviceId) return false;
  if (mockService.thinkTime !== template.thinkTime) return false;
  if (mockService.noMatchingRequestPreference !== template.noMatchingRequestPreference) return false;
  if (mockService.configurationId !== template.configurationId) return false;

  //Replicas, consider default values: undefined, null, 1.
  const msReplicasDefault = _.isNil(mockService.replicas) || mockService.replicas === 1;
  const templateReplicasDefault = _.isNil(template.replicas) || template.replicas === 1;

  // if both (or any) are different than default, compare, otherwise they are equals
  if ((!msReplicasDefault || !templateReplicasDefault) && mockService.replicas !== template.replicas) return false;

  // LiveSystemPort
  // if both (or any) have a value then compare, otherwise they are equals
  if (
    (!_.isNil(mockService.liveSystemPort) || !_.isNil(template.liveSystemPort)) &&
    mockService.liveSystemPort !== template.liveSystemPort
  )
    return false;

  // LiveSystemHost
  // if both (or any) have a value then compare, otherwise they are equals
  if (
    (!_.isNil(mockService.liveSystemHost) || !_.isNil(template.liveSystemHost)) &&
    mockService.liveSystemHost !== template.liveSystemHost
  )
    return false;

  // Data Settings
  // if both (or any) have a value then compare, otherwise they are equals
  if (
    (!_.isNil(mockService.dataSettings) || !_.isNil(template.dataSettings)) &&
    !_.isEqual(mockService.dataSettings, template.dataSettings)
  )
    return false;

  //Sort transactions by id first and then compare
  const msTransactions = _.sortBy(mockService.mockServiceTransactions, 'txnId');
  const templateTransactions = _.sortBy(template.mockServiceTransactions, 'txnId');
  if (!_.isEqual(msTransactions, templateTransactions)) return false;

  return true;
};

/**
 * Search input field value has changed handler.
 * @param event - input event
 * @param maxLength - maximum length of the service name
 * @param services - list of services to check for full match
 */
export const handleFieldChange = (event: any, maxLength: number, services: Service[]) => {
  const keyword: string = event.currentTarget.value as string;
  let fullMatch = false;
  let invalidErrorMessage = '';
  let isServiceNameValid = !!keyword.match('^[a-zA-Z0-9-\\/_&()?!:@#\\s]*$');
  if (isServiceNameValid && keyword.length > maxLength) {
    isServiceNameValid = false;
    invalidErrorMessage = ` ${i18n.t('servicePicker.serviceNameMaxLength', {
      maxLength: maxLength,
    })}`;
  }
  if (isServiceNameValid && keyword.length > 1) {
    isServiceNameValid = !!keyword.trim().length;
  }
  for (const service of services) {
    if (service.name.toLocaleLowerCase() === keyword.toLowerCase().trim()) {
      fullMatch = true;
      break;
    }
  }
  if (isServiceNameValid) {
    isServiceNameValid = !keyword.startsWith(' ');
  }
  return {
    searchTerm: keyword,
    fullMatchFound: fullMatch,
    isServiceNameValid: isServiceNameValid,
    invalidErrorMessage: invalidErrorMessage,
  };
};

/**
 * Update a virtual service list with metric data (hits). Returns updated list
 * @param mockServices
 * @param metrics
 */
export const updateMockServiceListWithHits = (mockServices: MockService[], metrics: Metrics[]) => {
  return _.map(mockServices, mockService => {
    const metricIndex = _.findIndex(metrics, metric => metric.serviceMockId === mockService.id);
    if (metricIndex !== -1) {
      mockService.hits = metrics[metricIndex].value;
    }
    return mockService;
  });
};

/**
 * Builds query for analytics cluster from a list of virtual services, so we can get the number of transactions (hits) count.
 * @param mockServices
 * @param workspaceId
 */
export const buildAnalyticsQueryForHits = (
  mockServices: MockService[],
  workspaceId: string,
): BlazeAnalyticsRequest[] => {
  if (_.isNil(mockServices) || mockServices.length === 0) {
    return null;
  }

  return mockServices
    .filter((ms: MockService) => ms.isNew === false)
    .map((ms: MockService) => {
      return {
        metricType:
          ms.type === TRANSACTIONAL ? ['SERVICE_MOCK_TRANSACTIONS_TOTAL_COUNT'] : ['TRANSACTIONS_TOTAL_COUNT'],
        serviceMockId: ms.serviceMockId ? ms.serviceMockId : ms.id,
        workspaceId: workspaceId,
      } as BlazeAnalyticsRequest;
    });
};

/**
 * Iterates through a virtual services list, transforms each virtual service to dto for analytics and fetches metrics data for them.
 * @param mockServices - list of virtual services to get metrics for.
 * @param workspaceId - selected ws id
 */
export const getAndProcessMetricsForMockServices = (mockServices: MockService[], workspaceId: string): any => {
  const metrics = buildAnalyticsQueryForHits(mockServices, workspaceId);
  if (!_.isNil(metrics) && metrics.length > 0) {
    store.dispatch(getHitAndMissesThunk(metrics));
  }
};

/**
 * Start polling the tracking service for services in configuring state.
 * @param mockServices
 */
export const initiateTrackingsForMockServices = (mockServices: MockService[]) => {
  if (_.isNil(mockServices) || mockServices.length === 0) {
    return;
  }

  // Save workspace id before we will start polling
  const workspaceId = getActiveWorkspaceId();

  mockServices
    .filter((mockService: MockService) => mockService.status === 'CONFIGURING')
    .forEach(async ms => {
      if (ms.lastActionTrackingId) {
        try {
          // Poll for up to ~10 minutes
          const trackingResult = await pollTrackingService(ms.lastActionTrackingId, 120, 5000);
          const errorMsg = _.get(trackingResult, 'data.result.errors');

          if (!_.isEmpty(errorMsg)) {
            throw new Error(errorMsg);
          } else {
            dispatchDeploymentSuccesMessage(ms, false);
          }
        } catch (error) {
          store.dispatch({ type: actionTypes.TOGGLE_IN_PROGRESS, serviceMockId: ms.id });
          dispatchDeploymentErrorMessage(ms, error);
        } finally {
          if (workspaceId === getActiveWorkspaceId()) {
            await store.dispatch(updateServiceMockThunk(ms.id));
            getAndProcessMetricsForMockServices([ms], workspaceId);
          }
        }
      }
    });
};

/**
 * Checks if endpoint is different between two services.
 * @param prevMockService
 * @param mockService
 */
export const hasEndpointChanged = (prevMockService: MockService, mockService: MockService) => {
  const previousEndpoint =
    prevMockService.endpointPreference === 'HTTPS' ? prevMockService.httpsEndpoint : prevMockService.httpEndpoint;

  const newEndpoint = mockService.endpointPreference === 'HTTPS' ? mockService.httpsEndpoint : mockService.httpEndpoint;

  return Boolean(previousEndpoint) && previousEndpoint !== newEndpoint;
};

/**
 * Add padding top styling to the text box using js
 */
export const addStyleUsingJs = (id: string, paddingValue: string) => {
  const elementVal = document.getElementById(id);
  if (elementVal) {
    elementVal.style.marginTop = paddingValue + 'px';
  }
};

/**
 * Display Upgrade mock warning icon for MockService.
 */
export const isUpgradeMockWarningDisplay = (mockService: MockService): boolean => {
  const usagePercentage = _.get(mockService, 'metadata.limits.percentage', 0);
  const isLocationUnknown = _.get(mockService, 'metadata.warnings.locationUnknown', false);
  if (
    (mockService.metaData.isDeployed &&
      !mockService.metaData.inProgress &&
      mockService.metadata &&
      mockService.metadata.warnings &&
      (mockService.metadata.warnings.upgradeAvailable || mockService.metadata.warnings.analyticsDataMissing)) ||
    usagePercentage >= TRANSACTION_THRESHOLD_LIMIT ||
    isLocationUnknown
  ) {
    return true;
  }
  return false;
};
/**
 * Find if an upgradable flag is true from a list of mocks
 */
export const isUpgradeMockAvailableFromListOfMocks = (mockServices: MockService[]): boolean => {
  let isUpgradableMock = false;
  for (const mockService of mockServices) {
    if (mockService.metadata && mockService.metadata.warnings && mockService.metadata.warnings.upgradeAvailable) {
      isUpgradableMock = true;
      break;
    }
  }
  return isUpgradableMock;
};

/**
 * Compare two virtual service lists by item names. We are comparing by name because it is the only property
 * that is same in drafts / virtual services. ID cannot be used for comparison. (DE23740)
 * @param msListA
 * @param msListB
 * @return equality result.
 */
export const isSameMockServiceList = (msListA?: MockService[] | null, msListB?: MockService[] | null) => {
  if (_.isNil(msListA) && _.isNil(msListB)) {
    return true;
  } else if (_.isNil(msListA) || _.isNil(msListB)) {
    return false;
  }

  const namesA = msListA.map(item => item.name);
  const namesB = msListB.map(item => item.name);
  return _.isEqual(namesA, namesB);
};

/**
 * Gets virtual service id.
 * @param mockService
 */
export const getMockServiceId = (mockService: MockService) => {
  if (mockService.isDraft === true) {
    return mockService.serviceMockId;
  }

  return mockService.id;
};

export const isMockServiceRunning = (mockService: MockService | MockServiceTemplate) => {
  if (isTemplate(mockService)) {
    return false;
  }
  return _.get(mockService, 'status') === 'RUNNING';
};

export const getMockServiceEndpoint = (mockService: MockService) => {
  if (_.isNil(mockService)) {
    return null;
  }

  const useHttps = mockService.endpointPreference === 'HTTPS';
  const runnerSubType = useHttps ? 'HTTPS' : 'HTTP';

  // New services: resolve from the endpoints array
  if (Array.isArray(mockService.endpoints) && mockService.endpoints.length > 0) {
    const preferred = mockService.endpoints.find(
      ms =>
        ms.runnerType.toUpperCase() === RunnerType.HTTP.toUpperCase() &&
        ms.runnerSubType.toUpperCase() === runnerSubType,
    );
    // Fall back to any available HTTP endpoint if the preferred subType isn't found
    const fallback = mockService.endpoints.find(ms => ms.runnerType.toUpperCase() === RunnerType.HTTP.toUpperCase());
    return (preferred ?? fallback)?.endpoint ?? null;
  }

  // Old services: fall back to httpEndpoint / httpsEndpoint string fields
  if (useHttps) {
    return mockService.httpsEndpoint || mockService.httpEndpoint || null;
  }
  return mockService.httpEndpoint || mockService.httpsEndpoint || null;
};

/**
 * Apply the configuration to the broker configuration.
 * @param brokerConfig - The current broker configuration
 * @param configuration - The configuration to apply
 */
export const applyConfiguration = (brokerConfig: BrokerConfigDto, configuration: Configuration) => {
  //Replace the configuration object
  const updatedBrokerConfig: any = brokerConfig ? { ...brokerConfig } : {};
  const configMap: ConfigurationMap = configuration.configurationMap;

  const getConfigValue = (key: string, defaultValue: any) => (configMap[key] && `\$\{config.${key}\}`) ?? defaultValue;
  updatedBrokerConfig.hostname = getConfigValue('MSG_HOST_NAME', brokerConfig?.hostname);
  updatedBrokerConfig.port = getConfigValue('MSG_HOST_PORT', brokerConfig?.port);
  updatedBrokerConfig.channel = getConfigValue('MSG_BROKER_CHANNEL', brokerConfig?.channel);
  updatedBrokerConfig.queueManager = getConfigValue('MSG_BROKER_QUEUE_MNG', brokerConfig?.queueManager);
  updatedBrokerConfig.username = getConfigValue('MSG_USERNAME', brokerConfig?.username);
  updatedBrokerConfig.password = getConfigValue('MSG_PASSWORD', brokerConfig?.password);

  return updatedBrokerConfig;
};

/**
 * Handles the change of XML namespace items in the state.
 * @param items
 * @param state
 * @param onUpdateState
 */
export const handleNameSpaceChange = (items: XmlNamespaceItem[], state: any, onUpdateState: any) => {
  const updatedState = state;
  updatedState.dirty = true;
  updatedState.body = _.map(updatedState.body, item => {
    if (item.matcher === Matcher.matches_xml) {
      item.namespaces = items;
    }
    return item;
  });
  onUpdateState(updatedState);
};
